export * from './auth.types';
export * from './team.types';

// Tipos de Role do sistema
export const ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN' as const,
  REPRESENTANTE: 'REPRESENTANTE' as const,
  ASSOCIADO: 'ASSOCIADO' as const,
  SECRETARIA: 'SECRETARIA' as const,
  SUPERVISOR: 'SUPERVISOR' as const,
  RELACIONADOR: 'RELACIONADOR' as const,
  LIDER_VENDAS: 'LIDER_VENDAS' as const,
  USER: 'USER' as const,
  POS_VENDA: 'POS_VENDA' as const,
} as const;

export type Role = typeof ROLES[keyof typeof ROLES];

// Status possíveis do usuário
export type UserStatus = 'ACTIVE' | 'PENDING' | 'REJECTED';

// Interface base do usuário
export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: Role;
  status: UserStatus;
  tenant_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface AuthResponse {
  user: User;
  access_token: string;
  refresh_token: string;
  isFirstUser?: boolean;
}

export interface ResetPasswordData {
  email: string;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
  newPasswordConfirmation: string;
}

// Tipos para permissões
export interface UserPermissions {
  // Permissões de papéis
  isSuperAdmin: boolean;
  isAdmin: boolean;
  isAdministradorGeral: boolean;
  isSecretaria: boolean;
  isSupervisor: boolean;
  isRelacionador: boolean;
  isLiderVendas: boolean;
  isConsultor: boolean;
  isFinanceiro: boolean;
  isPosVenda: boolean;

  // Permissões granulares - Contratos
  podeCriarContrato: boolean;
  podeEditarContrato: boolean;
  podeVisualizarContratos: boolean;
  podeFinalizarVenda: boolean;
  podeRepassarCliente: boolean;
  podeReceberCliente: boolean;
  podeTransferirContrato: boolean;
  podeReceberContrato: boolean;
  podeAprovarContrato: boolean;
  podeRejeitarContrato: boolean;
  podeFinalizarContrato: boolean;
  podeExcluirContrato: boolean;
  podeVisualizarContratosTime: boolean;

  // Permissões granulares - Clientes
  podeCriarCliente: boolean;
  podeEditarCliente: boolean;
  podeVisualizarClientes: boolean;
  podeVisualizarTodosClientes: boolean;
  podeVisualizarClientesTime: boolean;

  // Permissões granulares - Documentos
  podeGerenciarDocumentos: boolean;
  podeVisualizarDocumentos: boolean;

  // Permissões granulares - Agenda
  podeGerenciarAgenda: boolean;
  podeConfirmarRealizacaoAgenda: boolean;
  podeVisualizarAgenda: boolean;

  // Permissões granulares - Sistema
  podeConvidarUsuarios: boolean;
  podeAcessarRelatorios: boolean;
  podeEditarConfiguracoes: boolean;
  podeGerenciarTimes: boolean;
  podeGerenciarGrupos: boolean;
  podeGerenciarBens: boolean;
  podeGerenciarComissoes: boolean;

  // Função de verificação
  verificarPermissao: (requiredPermissions: Role[]) => boolean;
}

// Tipos para navegação baseada em papéis
export interface NavigationItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string | null;
  permissions?: Role[];
}

export interface QuickAction {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  action: 'navigate' | 'modal';
  url?: string;
  modal?: string;
  queryParams?: Record<string, string>;
}

export interface RoleNavigation {
  primary: NavigationItem[];
  secondary: NavigationItem[];
  quickActions: QuickAction[];
}

export interface RoleNavigationConfig {
  [key: string]: RoleNavigation;
} 